import { useHelpContext } from "@bokio/contexts/HelpContext/HelpContext";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { LoadingProgressText } from "@bokio/designsystem/components/Loading/LoadingProgressText";
import { Notice } from "@bokio/designsystem/components/Notice/Notice";
import GeneralLangFactory from "@bokio/lang/GeneralLangFactory";

import type { FC } from "react";

import * as styles from "./helpStyle.scss";

const getEmbedUrl = (pageId: string): string => {
	const baseUrl = window.config.websiteUrl.replace(/\/$/, "");
	return `${baseUrl}/umbraco/surface/FaqListSurface/GetAsEmbed?id=${pageId}`;
};

export interface HelpArticleProps {
	onGoBack?: () => void;
}

export const HelpArticle: FC<HelpArticleProps> = ({ onGoBack }) => {
	const { activeHelpArticle, openArticleIsLoading, openArticleIsError } = useHelpContext();

	const lang = GeneralLangFactory();
	if (openArticleIsError) {
		return (
			<div className={styles.helpPageInner}>
				<div className={styles.articleNav}>
					<a className={styles.backLink} onClick={onGoBack}>
						<Icon name="left-open" size="12" />
						{lang.Back}
					</a>
				</div>
				<div className={styles.helpPageContent}>
					<Notice color="error">{lang.HelpCenter_ArticleError}</Notice>
				</div>
			</div>
		);
	}

	const content =
		openArticleIsLoading || !activeHelpArticle ? (
			<div style={{ padding: "20px" }}>
				<LoadingProgressText text={lang.Loading} width="100%" />
			</div>
		) : (
			<div className={styles.helpPageArticleContainer}>
				<div className={styles.articleNav}>
					<a className={styles.backLink} onClick={onGoBack}>
						<Icon name="left-open" size="12" />
						{lang.Back}
					</a>
					<a
						role="button"
						className={styles.helpPageNewWindow}
						target="_blank"
						href={activeHelpArticle.Url}
						rel="noopener noreferrer"
					>
						{lang.OpenInNewTab}
						<Icon name="external-link" />
					</a>
				</div>
				{/*
				 * Security notes for sandbox="allow-same-origin allow-scripts":
				 * 1. The iframe src is CROSS-ORIGIN (PublicWeb), not same-origin or blob URL
				 * 2. Cross-origin scripts cannot access frameElement, so cannot remove sandbox
				 * 3. PublicWeb has CSP frame-src restricting nested iframes to YouTube only in FaqEmbed.cshtml
				 * 4. allow-same-origin is needed for YouTube embeds to work (they use Cache API)
				 * 5. allow-scripts is for the YouTube player to work
				 * 6. allow-popups is needed so that links in the help articles can open in a new tab
				 * 7. allow-popups-to-escape-sandbox ensures opened tabs function normally without inheriting sandbox restrictions
				 * 8. allow-presentation is needed for Chromecast support, but mostly here to supress warnings in the console about it
				 */}
				<iframe
					key={activeHelpArticle.Id}
					src={getEmbedUrl(activeHelpArticle.Id)}
					className={styles.helpPageArticleIframe}
					title={activeHelpArticle.Question}
					//eslint-disable-next-line @eslint-react/dom-no-unsafe-iframe-sandbox
					sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-presentation"
				/>
			</div>
		);
	return <div className={styles.helpPage}>{content}</div>;
};
